import {
  Home, Building2, Warehouse, MapPin,
  Youtube, Glasses, ImagePlus, FileImage,
  AlignLeft, Tag, Layers, BedDouble, Bath,
  Ruler, DollarSign, User, Users, Sofa,
  Phone, MessageSquare, Waves, Car, Trees, PawPrint,
  CheckCircle2, ChevronRight, MessageCircleOff, MessageCircle,
  Star, StarOff,
} from 'lucide-react';

export {
  Home, Building2, Warehouse, MapPin,
  Youtube, Glasses, ImagePlus, FileImage,
  AlignLeft, Tag, Layers, BedDouble, Bath,
  Ruler, DollarSign, User, Users, Sofa,
  Phone, MessageSquare, Waves, Car, Trees, PawPrint,
  CheckCircle2, ChevronRight, MessageCircleOff, MessageCircle,
  Star, StarOff,
};

// ── Constants ─────────────────────────────────────────────────────────────────

export const OWNER_OPTIONS = [
  { value: 'owner', label: 'เจ้าของขายเอง', Icon: User  },
  { value: 'agent', label: 'ตัวแทนขาย',     Icon: Users },
];

export const FURNITURE_OPTIONS = [
  { value: true,  label: 'มีเฟอร์นิเจอร์',   Icon: Sofa    },
  { value: false, label: 'ไม่มีเฟอร์นิเจอร์', Icon: StarOff },
];

export const makeFirstHandOptions = (title) => [
  { value: true,  label: `${title}มือหนึ่ง`, Icon: Star    },
  { value: false, label: `${title}มือสอง`,   Icon: StarOff },
];

export const ROOM_OPTIONS = ['1', '2', '3', '4', '5'];

export const AMENITY_OPTIONS = [
  { id: 'swim',    value: 'สระว่ายน้ำ',       Icon: Waves },
  { id: 'parking', value: 'โรงจอดรถ',          Icon: Car   },
  { id: 'central', value: 'ส่วนกลาง',           Icon: Trees },
  { id: 'pets',    value: 'เลี้ยงสัตว์เลี้ยงได้', Icon: PawPrint },
];

// ── Building blocks ───────────────────────────────────────────────────────────

/** Section card: white card with label + optional inline error */
export function FormSection({ title, required, error, children, className = '' }) {
  return (
    <div className={`w-full bg-white rounded-2xl border shadow-sm p-4 transition-colors ${
      error ? 'border-red-300' : 'border-gray-100'
    } ${className}`}>
      {title && (
        <p className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-1">
          {title}
          {required && <span className="text-red-400">*</span>}
        </p>
      )}
      {children}
      {error && (
        <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

/** Full-width error banner for API/auth errors */
export function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="w-full bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-600">
      {message}
    </div>
  );
}

/** Full-width success banner */
export function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div className="w-full bg-green-50 border border-green-200 rounded-2xl px-4 py-3 text-sm text-green-700 flex items-center gap-2">
      <CheckCircle2 size={16} className="shrink-0" />
      {message}
    </div>
  );
}

/** Controlled text / number input */
export function FormInput({ label, className = '', ...props }) {
  return (
    <input
      className={`w-full h-12 bg-gray-50 rounded-xl px-4 text-sm outline-none border border-gray-100 focus:border-postperty transition-colors ${className}`}
      {...props}
    />
  );
}

/** Two-option pill toggle using lucide icons */
export function BinaryToggle({ value, options, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(({ value: v, label, Icon }) => {
        const active = value === v;
        return (
          <button
            key={String(v)}
            type="button"
            onClick={() => onChange(v)}
            className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all border ${
              active
                ? 'bg-postperty text-white border-postperty shadow-sm'
                : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-postperty hover:text-postperty'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Area unit toggle: ตร.ม. / ตร.ว. */
export function UnitToggle({ value, onChange }) {
  const opts = [
    { unit: 'ตารางเมตร', short: 'ตร.ม.' },
    { unit: 'ตารางวา',   short: 'ตร.ว.' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 mt-2">
      {opts.map(({ unit, short }) => (
        <button
          key={unit}
          type="button"
          onClick={() => onChange(unit)}
          className={`h-10 rounded-xl flex items-center justify-center gap-2 text-sm transition-all border ${
            value === unit
              ? 'bg-postperty text-white border-postperty'
              : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-postperty hover:text-postperty'
          }`}
        >
          <Ruler size={14} />
          {short}
        </button>
      ))}
    </div>
  );
}

/** Primary action button */
export function FormButton({ onClick, disabled, children, variant = 'primary' }) {
  const base = 'w-full h-12 rounded-xl flex items-center justify-center gap-2 font-medium text-sm transition-all';
  const styles = variant === 'primary'
    ? 'bg-postperty text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm'
    : 'bg-gray-100 text-gray-600 hover:bg-gray-200';
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}
